import { useEffect, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getDefaultMaxOpenOrders,
  getScorecardTargets,
  getSupportPhone,
  updateDefaultMaxOpenOrders,
  updateOpsUserMaxOpenOrders,
  updateOpsUserStatus,
  updateScorecardTargets,
  updateSupportPhone,
} from '../../api/endpoints/ops';
import { staticQueryPolicy } from '../../api/queryPolicies';
import type { OpsUser, ScorecardTargets } from '../../api/types';
import { ErrorState, LoadingState } from '../../components/ui/AsyncState';
import { useMutationHook } from '../../hooks/useMutationHook';
import { OwnerChip } from '../ownership/OwnerChip';
import { opsUserName, useOpsUser, useOpsUsers } from '../ownership/opsUser';
import {
  CLAIM_TARGET_MAX,
  CLAIM_TARGET_MIN,
  DRIVER_TARGET_MAX,
  DRIVER_TARGET_MIN,
  POD_CLOSE_TARGET_MAX,
  POD_CLOSE_TARGET_MIN,
  WEEKLY_FULFILLED_MAX,
  WEEKLY_FULFILLED_MIN,
  parseCap,
  parseCapOverride,
  parseTargetInt,
  validateSupportPhone,
} from './opsSettingsValidation';
import styles from './SettingsView.module.css';
import team from './OpsTeamTab.module.css';
import { Button } from '../../components/ui/Button';

const supportPhoneKey = ['admin', 'settings', 'support-phone'];
const defaultCapKey = ['admin', 'settings', 'default-max-open-orders'];

function SupportPhoneField({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: supportPhoneKey,
    queryFn: getSupportPhone,
    ...staticQueryPolicy,
  });
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (data) setValue(data);
  }, [data]);

  const save = useMutationHook((phone: string) => updateSupportPhone(phone), {
    successMessage: 'Support phone updated.',
    invalidateKeys: [supportPhoneKey],
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const problem = validateSupportPhone(value);
    setError(problem);
    if (!problem) save.mutate(value.trim());
  };

  return (
    <form className={team.field} onSubmit={submit} noValidate>
      <label className={styles.label} htmlFor="supportPhone">
        {t('settings.opsTeam.supportPhoneLabel')}
      </label>
      <div className={team.inline}>
        <input
          id="supportPhone"
          className={team.input}
          type="tel"
          placeholder="+918522803275"
          value={isLoading ? '' : value}
          disabled={!canEdit || isLoading || save.isLoading}
          onChange={(e) => setValue(e.target.value)}
        />
        {canEdit && (
          <Button type="submit" variant="secondary" size="sm" disabled={save.isLoading || isLoading}>
            {save.isLoading ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>
      {isError && <span className={team.fieldError}>{t('settings.opsTeam.supportPhoneLoadError')}</span>}
      {error && (
        <span className={team.fieldError} role="alert">
          {error}
        </span>
      )}
    </form>
  );
}

function DefaultCapField({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: defaultCapKey,
    queryFn: getDefaultMaxOpenOrders,
    ...staticQueryPolicy,
  });
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (data != null) setValue(String(data));
  }, [data]);

  const save = useMutationHook((cap: number) => updateDefaultMaxOpenOrders(cap), {
    successMessage: 'Default open-order cap updated.',
    invalidateKeys: [defaultCapKey, ['admin', 'ops-users']],
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = parseCap(value);
    if ('error' in parsed) {
      setError(parsed.error);
      return;
    }
    setError(null);
    save.mutate(parsed.value);
  };

  return (
    <form className={team.field} onSubmit={submit} noValidate>
      <label className={styles.label} htmlFor="defaultCap">
        {t('settings.opsTeam.defaultCapLabel')}
      </label>
      <div className={team.inline}>
        <input
          id="defaultCap"
          className={team.input}
          inputMode="numeric"
          value={isLoading ? '' : value}
          disabled={!canEdit || isLoading || save.isLoading}
          onChange={(e) => setValue(e.target.value)}
        />
        {canEdit && (
          <Button type="submit" variant="secondary" size="sm" disabled={save.isLoading || isLoading}>
            {save.isLoading ? 'Saving…' : 'Save'}
          </Button>
        )}
      </div>
      {isError && <span className={team.fieldError}>{t('settings.opsTeam.defaultCapLoadError')}</span>}
      {error && (
        <span className={team.fieldError} role="alert">
          {error}
        </span>
      )}
    </form>
  );
}

function StatusCell({ user, canEdit }: { user: OpsUser; canEdit: boolean }) {
  const save = useMutationHook((status: 'ACTIVE' | 'AWAY') => updateOpsUserStatus(user.id, status), {
    successMessage: (u) => `${opsUserName(u)} is now ${u.status === 'AWAY' ? 'away' : 'active'}.`,
    invalidateKeys: [['admin', 'ops-users']],
  });
  const option = (status: 'ACTIVE' | 'AWAY', label: string) => {
    const selected = user.status === status;
    const tone = status === 'ACTIVE' ? team.statusActive : team.statusAway;
    return (
      <button
        type="button"
        className={`${team.statusOption} ${selected ? tone : ''}`}
        aria-pressed={selected}
        disabled={!canEdit || selected || save.isLoading}
        onClick={() => save.mutate(status)}
      >
        {label}
      </button>
    );
  };
  return (
    <div className={team.statusToggle} role="group" aria-label={`Status for ${opsUserName(user)}`}>
      {option('ACTIVE', 'Active')}
      {option('AWAY', 'Away')}
    </div>
  );
}

function CapCell({ user, canEdit }: { user: OpsUser; canEdit: boolean }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(user.maxOpenOrders != null ? String(user.maxOpenOrders) : '');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setValue(user.maxOpenOrders != null ? String(user.maxOpenOrders) : '');
  }, [user.maxOpenOrders]);

  const save = useMutationHook((cap: number | null) => updateOpsUserMaxOpenOrders(user.id, cap), {
    successMessage: (u) =>
      u.maxOpenOrders == null
        ? `${opsUserName(u)} now uses the default cap.`
        : `${opsUserName(u)}'s cap set to ${u.maxOpenOrders}.`,
    invalidateKeys: [['admin', 'ops-users']],
  });

  if (!canEdit) {
    return (
      <span className={team.mono}>
        {user.maxOpenOrders ?? <span className={team.muted}>{t('settings.opsTeam.capDefault')}</span>}
      </span>
    );
  }

  const dirty = value.trim() !== (user.maxOpenOrders != null ? String(user.maxOpenOrders) : '');
  const submit = () => {
    const parsed = parseCapOverride(value);
    if ('error' in parsed) {
      setError(parsed.error);
      return;
    }
    setError(null);
    save.mutate(parsed.value);
  };

  return (
    <div>
      <div className={team.inline}>
        <input
          aria-label={`Cap override for ${opsUserName(user)}`}
          className={team.capInput}
          inputMode="numeric"
          placeholder={t('settings.opsTeam.capDefault')}
          value={value}
          disabled={save.isLoading}
          onChange={(e) => setValue(e.target.value)}
        />
        <button
          type="button"
          className={team.smallBtn}
          disabled={!dirty || save.isLoading}
          onClick={submit}
        >
          {t('common.actions.save')}
        </button>
      </div>
      {error && (
        <span className={team.fieldError} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

function ScorecardTargetsCard({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const queryKey = ['admin', 'settings', 'scorecard-targets'];
  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: getScorecardTargets,
    ...staticQueryPolicy,
  });

  const [weeklyFulfilled, setWeeklyFulfilled] = useState('');
  const [claimTarget, setClaimTarget] = useState('');
  const [driverTarget, setDriverTarget] = useState('');
  const [podCloseTarget, setPodCloseTarget] = useState('');
  const [errors, setErrors] = useState<{ [k: string]: string | null }>({});

  useEffect(() => {
    if (data) {
      setWeeklyFulfilled(String(data.weeklyFulfilledTarget));
      setClaimTarget(String(data.claimTargetMinutes));
      setDriverTarget(String(data.driverTargetMinutes));
      setPodCloseTarget(String(data.podCloseTargetHours));
    }
  }, [data]);

  const save = useMutationHook((targets: ScorecardTargets) => updateScorecardTargets(targets), {
    successMessage: 'Scorecard targets updated.',
    invalidateKeys: [queryKey, ['admin', 'scorecard']],
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const wf = parseTargetInt(
      weeklyFulfilled,
      WEEKLY_FULFILLED_MIN,
      WEEKLY_FULFILLED_MAX,
      'Weekly fulfilled target',
    );
    const ct = parseTargetInt(claimTarget, CLAIM_TARGET_MIN, CLAIM_TARGET_MAX, 'Claim target');
    const dt = parseTargetInt(
      driverTarget,
      DRIVER_TARGET_MIN,
      DRIVER_TARGET_MAX,
      'Driver assignment target',
    );
    const pc = parseTargetInt(
      podCloseTarget,
      POD_CLOSE_TARGET_MIN,
      POD_CLOSE_TARGET_MAX,
      'POD closure target',
    );

    const errs: { [k: string]: string | null } = {
      weeklyFulfilled: 'error' in wf ? wf.error : null,
      claimTarget: 'error' in ct ? ct.error : null,
      driverTarget: 'error' in dt ? dt.error : null,
      podCloseTarget: 'error' in pc ? pc.error : null,
    };
    setErrors(errs);

    if ('value' in wf && 'value' in ct && 'value' in dt && 'value' in pc) {
      save.mutate({
        weeklyFulfilledTarget: wf.value,
        claimTargetMinutes: ct.value,
        driverTargetMinutes: dt.value,
        podCloseTargetHours: pc.value,
      });
    }
  };

  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>{t('settings.opsTeam.scorecardTitle')}</h3>
      <p className={styles.cardHint}>
        {t('settings.opsTeam.scorecardHint')}
      </p>
      <form onSubmit={submit} noValidate>
        <div className={team.grid}>
          <div className={team.field}>
            <label className={styles.label} htmlFor="weeklyFulfilledTarget">
              {t('settings.opsTeam.weeklyFulfilledLabel')}
            </label>
            <input
              id="weeklyFulfilledTarget"
              className={team.input}
              inputMode="numeric"
              value={isLoading ? '' : weeklyFulfilled}
              disabled={!canEdit || isLoading || save.isLoading}
              onChange={(e) => setWeeklyFulfilled(e.target.value)}
            />
            {errors.weeklyFulfilled && (
              <span className={team.fieldError} role="alert">
                {errors.weeklyFulfilled}
              </span>
            )}
          </div>

          <div className={team.field}>
            <label className={styles.label} htmlFor="claimTargetMinutes">
              {t('settings.opsTeam.claimTargetLabel')}
            </label>
            <input
              id="claimTargetMinutes"
              className={team.input}
              inputMode="numeric"
              value={isLoading ? '' : claimTarget}
              disabled={!canEdit || isLoading || save.isLoading}
              onChange={(e) => setClaimTarget(e.target.value)}
            />
            {errors.claimTarget && (
              <span className={team.fieldError} role="alert">
                {errors.claimTarget}
              </span>
            )}
          </div>

          <div className={team.field}>
            <label className={styles.label} htmlFor="driverTargetMinutes">
              {t('settings.opsTeam.driverTargetLabel')}
            </label>
            <input
              id="driverTargetMinutes"
              className={team.input}
              inputMode="numeric"
              value={isLoading ? '' : driverTarget}
              disabled={!canEdit || isLoading || save.isLoading}
              onChange={(e) => setDriverTarget(e.target.value)}
            />
            {errors.driverTarget && (
              <span className={team.fieldError} role="alert">
                {errors.driverTarget}
              </span>
            )}
          </div>

          <div className={team.field}>
            <label className={styles.label} htmlFor="podCloseTargetHours">
              {t('settings.opsTeam.podCloseTargetLabel')}
            </label>
            <input
              id="podCloseTargetHours"
              className={team.input}
              inputMode="numeric"
              value={isLoading ? '' : podCloseTarget}
              disabled={!canEdit || isLoading || save.isLoading}
              onChange={(e) => setPodCloseTarget(e.target.value)}
            />
            {errors.podCloseTarget && (
              <span className={team.fieldError} role="alert">
                {errors.podCloseTarget}
              </span>
            )}
          </div>
        </div>

        {isError && (
          <span className={team.fieldError}>{t('settings.opsTeam.scorecardLoadError')}</span>
        )}

        {canEdit && (
          <div style={{ marginTop: '16px' }}>
            <Button
              type="submit"
              variant="secondary" size="sm"
              disabled={save.isLoading || isLoading}
            >
              {save.isLoading ? 'Saving targets…' : 'Save targets'}
            </Button>
          </div>
        )}
        {!canEdit && (
          <div className={team.readOnly}>{t('settings.opsTeam.managersOnly')}</div>
        )}
      </form>
    </div>
  );
}

/**
 * Settings → Ops team: support phone + default open-order cap, scorecard targets,
 * and the ops roster with ACTIVE/AWAY status and per-person cap overrides.
 * Everyone can read; only ops managers can edit.
 */
export function OpsTeamTab() {
  const { t } = useTranslation();
  const { opsUser, isManager } = useOpsUser();
  const users = useOpsUsers();

  return (
    <>
      <div className={styles.card}>
        <h3 className={styles.cardTitle}>{t('settings.opsTeam.supportTitle')}</h3>
        <p className={styles.cardHint}>
          {t('settings.opsTeam.supportHint')}
        </p>
        <div className={team.grid}>
          <SupportPhoneField canEdit={isManager} />
          <DefaultCapField canEdit={isManager} />
        </div>
        {!isManager && (
          <div className={team.readOnly}>{t('settings.opsTeam.managersOnly')}</div>
        )}
      </div>

      <ScorecardTargetsCard canEdit={isManager} />

      <div className={styles.card}>
        <h3 className={styles.cardTitle}>{t('settings.opsTeam.teamTitle')}</h3>
        <p className={styles.cardHint}>
          {t('settings.opsTeam.teamHint')}
        </p>
        {users.isLoading && <LoadingState label={t('settings.opsTeam.loadingTeam')} />}
        {users.isError && !users.data && (
          <ErrorState title={t('settings.opsTeam.teamLoadError')} onRetry={() => users.refetch()} />
        )}
        {users.data && (
          <div className={team.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('settings.opsTeam.colName')}</th>
                  <th>{t('settings.opsTeam.colRole')}</th>
                  <th>{t('settings.opsTeam.colOpenOrders')}</th>
                  <th>{t('settings.opsTeam.colStatus')}</th>
                  <th>{t('settings.opsTeam.colCapOverride')}</th>
                </tr>
              </thead>
              <tbody>
                {users.data.map((u) => {
                  const atCap = u.openOrderCount >= u.effectiveMaxOpenOrders;
                  return (
                    <tr key={u.id}>
                      <td>
                        <OwnerChip owner={u} meId={opsUser?.id} />
                        {!u.phoneValid && <div className={team.warn}>{t('settings.opsTeam.phoneInvalid')}</div>}
                      </td>
                      <td>{u.role === 'OPS_MANAGER' ? 'Manager' : 'Executive'}</td>
                      <td className={`${team.mono} ${atCap ? team.atCap : ''}`}>
                        {u.openOrderCount} / {u.effectiveMaxOpenOrders}
                      </td>
                      <td>
                        <StatusCell user={u} canEdit={isManager} />
                      </td>
                      <td>
                        <CapCell user={u} canEdit={isManager} />
                      </td>
                    </tr>
                  );
                })}
                {users.data.length === 0 && (
                  <tr>
                    <td colSpan={5} className={team.muted}>
                      {t('settings.opsTeam.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
